/**
 * Bootstrap: seeds default data into the NextGraph wallet on first use.
 *
 * Called once after NG connection + shapes ready. If the wallet already
 * has events/users, it's a returning user — skip seeding.
 *
 * ONE DOCUMENT PER ENTITY (rule_document-per-entity): every seeded entity is
 * created as its OWN document in its scope via the SDK create (`createEntityDoc`,
 * injected). Events live in the PUBLIC scope; profiles + participations in the
 * PROTECTED scope. The created document NURIs are returned so the caller can add
 * them to the live subscription set (reactivity).
 */

import type { Nuri } from '@ng-eventually/polyfill';
import { openDocumentInbox } from './storeRegistry';
import {
  seedEvents,
  seedUsers,
} from '../data/seedData';
import { writeEntity, ENTITY_TYPE, str, int, flt, bool } from '../data/entityWrites';
import { fixtureSeedEnabled } from './autoSeed';

/** Scope of a seed entity + how to create its own document (SDK create). */
export type Scope = 'public' | 'protected' | 'private';
/** Placement is named by SCOPE ALONE — the session that seeds owns what it seeds. */
export type CreateEntityDoc = (scope: Scope) => Promise<Nuri>;

export interface BootstrapResult {
  seeded: boolean;
  userIdMap: Map<string, string>;
  eventIdMap: Map<string, string>;
  /** Every per-entity document created, by scope — register these to subscribe. */
  createdDocs: { public: Nuri[]; protected: Nuri[] };
}

/**
 * Seed default data — ONE DOCUMENT PER ENTITY (rule_document-per-entity), written
 * DIRECTLY into each entity's own document (see `entityWrites.writeEntity`). The
 * created document NURIs are returned so the caller registers them into the read
 * model's doc set for the union READ.
 *
 * `walletHasData` tells the seed whether the wallet already carries entities (a
 * returning user → skip). The caller computes it from what it has already read.
 */
export async function bootstrapWallet(
  walletHasData: boolean,
  createEntityDoc: CreateEntityDoc,
): Promise<BootstrapResult> {
  const createdDocs: { public: Nuri[]; protected: Nuri[] } = { public: [], protected: [] };
  // THE SEED IS OFF (see `fixtureSeedEnabled`). This is the one place fixtures
  // enter a wallet, so the switch is enforced HERE rather than at each caller:
  // every route — the opt-in auto-seed, an explicit `loadTestData()` — funnels
  // through this function, and none of them can write around it. Callers get the
  // ordinary "nothing was seeded" answer, which is exactly true.
  if (!fixtureSeedEnabled()) {
    console.log('[Bootstrap] Fixture seed is disabled — nothing written to the wallet');
    return { seeded: false, userIdMap: new Map(), eventIdMap: new Map(), createdDocs };
  }
  // Already has data → returning user, nothing to seed
  if (walletHasData) {
    console.log('[Bootstrap] Wallet already has data — skipping seed');
    return { seeded: false, userIdMap: new Map(), eventIdMap: new Map(), createdDocs };
  }

  console.log('[Bootstrap] First time for this wallet — seeding per-entity docs...');

  // OWNERSHIP: "load test data into MY wallet" — every seeded entity is created
  // in a SCOPE of the signed-in session, which therefore owns it. That is what
  // makes the PROTECTED fixtures (user profiles, participations) round-trip:
  // reading is possession of the key, and the creator holds what it created.
  // The seed users are FIXTURES, not accounts anyone signs in as — they exist to
  // be read, never to be addressed as a person. PUBLIC events are readable by
  // whoever reaches them, ownership notwithstanding.

  // SEED FOOTPRINT (perf). Each entity is its OWN document, and `docCreate` is a
  // round-trip that does not overlap with the next one, so the seed cost grows
  // LINEARLY with the number of documents (measured around 2s each on our test
  // setup — an observation, not a contract). Seeding the full
  // fixture (14 users + 5 events + 5 participations = 24 docs) blows past the test
  // step budget. So the CONNECTED seed writes only what the app/@data needs to be
  // exercised: ALL events (looked up by title), a FEW user profiles ("wallet has
  // users" + participant rendering), and NO seed participations — the inscription
  // scenarios create their own participation live via joinEvent, and each event
  // carries its own `participantCount`. The @ui/demo path still uses the full
  // fixture (seedData) directly; only this NG bootstrap trims for round-trip speed.
  const SEED_USER_LIMIT = 3;
  const usersToSeed = seedUsers.slice(0, SEED_USER_LIMIT);

  // Serialize the FIRST create, then fan out: it warms whatever the scope needs
  // to exist before a document can be filed in it, so the concurrent creates that
  // follow don't race to establish the same thing twice. A creation that cannot
  // be recorded THROWS (it never returns a reference that would read empty
  // forever), so a failure here aborts the seed instead of half-seeding it.
  const firstUserGraph = await createEntityDoc('protected');

  // Users — one PROTECTED document each. The written subject IRI is the entity's
  // stable `@id`, kept in the id map.
  const userIdMap = new Map<string, string>();
  await Promise.all(usersToSeed.map(async (u, i) => {
    const graph = i === 0 ? firstUserGraph : await createEntityDoc('protected');
    createdDocs.protected.push(graph);
    const id = await writeEntity(graph, ENTITY_TYPE.user, {
      name: str(u.name), initials: str(u.initials), username: str(u.username),
      role: str(u.role), isPublic: bool(u.isPublic),
    });
    userIdMap.set(u.id, id);
  }));
  console.log('[Bootstrap] Seeded', userIdMap.size, 'users');

  // Events — one PUBLIC document each (all of them: looked up by title in @data).
  const eventIdMap = new Map<string, string>();
  await Promise.all(seedEvents.map(async (e) => {
    const graph = await createEntityDoc('public');
    // An event is a document people SIGN UP TO, so its owner opens its inbox —
    // a document only has one if its owner did. Without this, a registrant's
    // deposit has nothing to reach. Only events get one (not every entity).
    await openDocumentInbox(graph);
    createdDocs.public.push(graph);
    const id = await writeEntity(graph, ENTITY_TYPE.event, {
      title: str(e.title), description: str(e.description), date: str(e.date),
      location: str(e.location), distance: flt(e.distance),
      participantCount: int(e.participantCount),
      coverImage: str(e.coverImage),
    });
    eventIdMap.set(e.id, id);
    // The seeded event is NOT announced anywhere: there is no discovery index to
    // submit it to. Someone who did not run this seed therefore does not see these
    // events — they see their own. Restoring that requires the directory document.
  }));
  console.log('[Bootstrap] Seeded', eventIdMap.size, 'events (participations created live)');

  return { seeded: true, userIdMap, eventIdMap, createdDocs };
}
